import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
export const defaultDemoViewerState = {
    hasLeaguePass: false,
    isLoggedIn: false,
    showBettingOdds: true,
};
const demoViewerQueryKeys = [
    "loggedIn",
    "leaguePass",
    "bettingOdds",
];
function isPrototypePath(pathname) {
    return pathname === "/home" || pathname === "/home/";
}
function setNonDefaultDemoViewerQuery(query, viewer) {
    for (const key of demoViewerQueryKeys) {
        query.delete(key);
    }
    if (viewer.isLoggedIn) {
        query.set("loggedIn", "1");
    }
    if (viewer.hasLeaguePass) {
        query.set("leaguePass", "1");
    }
    if (viewer.showBettingOdds === false) {
        query.set("bettingOdds", "0");
    }
}
export function normalizedDemoViewerState(viewer) {
    return viewer.hasLeaguePass
        ? {
            ...viewer,
            hasLeaguePass: true,
            isLoggedIn: true,
            showBettingOdds: viewer.showBettingOdds !== false,
        }
        : {
            ...viewer,
            showBettingOdds: viewer.showBettingOdds !== false,
        };
}
export function parseDemoViewerState(search = "") {
    const query = new URLSearchParams(search);
    return normalizedDemoViewerState({
        hasLeaguePass: query.get("leaguePass") === "1",
        isLoggedIn: query.get("loggedIn") === "1",
        showBettingOdds: query.get("bettingOdds") !== "0",
    });
}
export function viewingAccessForGame(viewer, isNationallyBroadcast) {
    if (isNationallyBroadcast) {
        return "broadcast-only";
    }
    if (viewer.hasLeaguePass) {
        return "league-pass";
    }
    return viewer.isLoggedIn
        ? "league-pass-unentitled"
        : "league-pass-signed-out";
}
export function primaryViewingProvider(game) {
    const nationalProvider = game.nationalBroadcasters?.[0];
    if (nationalProvider) {
        return nationalProvider;
    }
    const hasVerifiedLocalBroadcast = Boolean(game.broadcaster) ||
        Boolean(game.broadcasters?.length);
    return hasVerifiedLocalBroadcast ? "League Pass" : null;
}
export function viewingActionLabelForGame(game, viewer) {
    const nationalProvider = game.nationalBroadcasters?.[0];
    if (nationalProvider) {
        return `Watch on ${nationalProvider}`;
    }
    if (!primaryViewingProvider(game)) {
        return "Tap To Watch";
    }
    if (viewer.hasLeaguePass) {
        return "Watch on League Pass";
    }
    return viewer.isLoggedIn ? "Get League Pass" : "Sign in to watch";
}
export function hrefWithDemoViewer(href, viewer) {
    const url = new URL(href, "https://feed-lab.invalid");
    for (const key of demoViewerQueryKeys) {
        url.searchParams.delete(key);
    }
    if (isPrototypePath(url.pathname)) {
        setNonDefaultDemoViewerQuery(url.searchParams, viewer);
    }
    return `${url.pathname}${url.search}${url.hash}`;
}
const DemoViewerContext = createContext({
    setViewer: () => undefined,
    viewer: defaultDemoViewerState,
});
export function DemoViewerProvider({ children, }) {
    const [viewer, setViewerState] = useState(() => typeof window === "undefined"
        ? defaultDemoViewerState
        : isPrototypePath(window.location.pathname)
            ? parseDemoViewerState(window.location.search)
            : defaultDemoViewerState);
    const setViewer = useCallback((nextViewer) => {
        setViewerState(normalizedDemoViewerState(nextViewer));
    }, []);
    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        for (const key of demoViewerQueryKeys) {
            query.delete(key);
        }
        if (isPrototypePath(window.location.pathname)) {
            setNonDefaultDemoViewerQuery(query, viewer);
        }
        const search = query.toString();
        window.history.replaceState(null, "", `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`);
    }, [viewer]);
    const value = useMemo(() => ({ setViewer, viewer }), [setViewer, viewer]);
    return (_jsx(DemoViewerContext.Provider, { value: value, children: children }));
}
export function useDemoViewer() {
    return useContext(DemoViewerContext);
}
